import { Badge, Button, Icon, Image } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { constants } from '@hero/configs';
import { PRODUCT_TYPES, SECONDARY_PRODUCT_TYPES } from '@hero/constants';
import { useCreatePriceLabel, useGetProductImages } from '@hero/hooks';
import { PriceBreakdownDropdown } from '@hero/molecules';

import * as S from './style';

export function ProductCard({ product, isSelected = false, onClick }) {
  const { t } = useTranslation();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const images = useGetProductImages({ product });
  const { priceLabel, minPrice, maxPrice, regularPriceLabel, isCAActMustBeApplied } =
    useCreatePriceLabel(product);
  const isProduct = !SECONDARY_PRODUCT_TYPES.includes(product?.meta?.productType);

  const selectedImage = images?.[selectedImageIndex]?.url || '';

  return (
    <S.ProductCard isSelected={isSelected} onClick={onClick}>
      <S.ProductCardHeader>
        <S.ProductCardImage
          ratio="138:151"
          isContain={product?.meta?.productType === PRODUCT_TYPES.GIFT_CARD}
        >
          <Image
            id="product-card-image"
            src={selectedImage}
            alt={product.name}
            width="100%"
            height="100%"
          />
        </S.ProductCardImage>
        {images?.length > 1 && (
          <>
            <S.CardGrid
              itemCount={images.length}
              onMouseLeave={() => {
                setSelectedImageIndex(0);
              }}
            >
              {images?.map((image, index) => (
                <S.CardGridItem
                  key={image?.url}
                  onMouseEnter={() => {
                    setSelectedImageIndex(index);
                  }}
                />
              ))}
              {product?.meta?.isInternational && (
                <Badge color="bgStrong">
                  <Icon name="globe" />
                  {t('international')}
                </Badge>
              )}
            </S.CardGrid>
            {isProduct && (
              <S.Navigation>
                {images.map((image, mapImageIndex) => (
                  <S.NavigationItem
                    key={image?.url}
                    active={selectedImageIndex === mapImageIndex}
                    onMouseEnter={e => {
                      e.stopPropagation();
                      setSelectedImageIndex(mapImageIndex);
                    }}
                    onClick={e => {
                      e.stopPropagation();
                      setSelectedImageIndex(mapImageIndex);
                    }}
                  />
                ))}
              </S.Navigation>
            )}
          </>
        )}
        {constants?.brands?.data[product.brand?.id]?.logo && (
          <S.ProductCardBrandImage>
            <Image
              src={product?.meta?.logoUrl || constants.brands.data[product.brand.id].logo}
              alt={product.name}
              height={22}
            />
          </S.ProductCardBrandImage>
        )}
        {product?.meta?.isInternational && (
          <Badge color="bgStrong">
            <Icon name="globe" />
            {t('international')}
          </Badge>
        )}
        {isSelected && (
          <Button id="button-add" variant="outlined">
            {t('addToCollection')}
          </Button>
        )}
        {isSelected && (
          <Button id="button-remove" color="error" variant="outlined">
            {t('remove')}
          </Button>
        )}
        <S.CheckInputWrapper isSelected={isSelected}>
          <Icon
            id="icon-success"
            name="success"
            color={isSelected ? 'var(--color-bg-default)' : 'var(--color-border-strong)'}
            size="lg"
          />
          <Icon id="icon-close" name="close" color={'var(--color-bg-default)'} size="lg" />
        </S.CheckInputWrapper>
      </S.ProductCardHeader>
      <S.ProductCardDetail>
        <S.ProductCardTitle variant="body1" dangerouslySetInnerHTML={{ __html: product.name }} />
        <S.ProductCardPrice>
          {(!Number.isNaN(minPrice) || !Number.isNaN(maxPrice)) && (
            <S.ProductCardPriceValue>
              <S.ProductCardPriceLabel variant="body1Bold">{priceLabel}</S.ProductCardPriceLabel>
              {regularPriceLabel && (
                <S.ProductCardRegularPriceLabel variant="body2" color="fg" emphasis="weaker">
                  {regularPriceLabel}
                </S.ProductCardRegularPriceLabel>
              )}
            </S.ProductCardPriceValue>
          )}
          {!product.orderable && (
            <S.ProductStockWrapper>
              <Badge
                variant="contained"
                status="active"
                text={t('outOfStock')}
                size="sm"
                color="warningWeak"
              />
            </S.ProductStockWrapper>
          )}
        </S.ProductCardPrice>
        {isCAActMustBeApplied && <PriceBreakdownDropdown product={product} />}
      </S.ProductCardDetail>
    </S.ProductCard>
  );
}

ProductCard.propTypes = {
  product: PropTypes.object,
  isSelected: PropTypes.bool,
  onClick: PropTypes.func
};

ProductCard.S = S;
